import type { CancellationChoice } from "../../../shared/cancellation_reasons.js";

/** Mirrors ZenpostBonusFeature / ZenpostZenlinksPage in server/zenpost_client.ts. */
export interface CancelRescueBonus {
  key: string;
  name: string;
}

export interface CancelRescueZenlinksPage {
  url: string;
  live: boolean;
}

/** Mirrors ZenpostCancelRescueAssets in server/zenpost_client.ts: what the user built (PIT-443). */
export interface CancelRescueAssets {
  contents: number;
  unpublished_contents: number;
  unpublished_kept_days: number;
  /** Posts still to go out; a refund ends access now, so it stops all of them. */
  scheduled_contents?: number;
  /** Those after the paid period, which is what stopping the renewal stops. */
  scheduled_after_access_ends?: number;
  brand_identity: boolean;
}

export interface CancellationLosses {
  title: string;
  /** `detail` is a smaller second line under the label. */
  items: { key: string; label: string; detail?: string }[];
}

type TranslateFn = (key: string, opts?: Record<string, string>) => string;

/**
 * Bonus keys with a name in the locale files. Any other key shows the name
 * Zenpost sent (the admin's label, in English), so a new bonus still appears.
 */
export const NAMED_BONUS_KEYS = [
  "logo_generator",
  "fixed_posts_generator",
  "instagram_bio",
  "bunker_course",
  "zenlinks",
] as const;

export function bonusLabel(key: string, fallbackName: string, t: TranslateFn): string {
  return (NAMED_BONUS_KEYS as readonly string[]).includes(key)
    ? t(`integrated.bonusName_${key}`)
    : fallbackName.trim();
}

/** "https://pages.zenpost.ai/l/slug/" → "pages.zenpost.ai/l/slug". */
export function displayPageUrl(url: string): string {
  return url.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

const count = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

/**
 * What the customer built, first in the list (PIT-443): the contents they created, the scheduled
 * posts that stop going out, and their brand. Saved contents and drafts are kept for Zenpost's
 * retention window and then deleted, and the screen says so with Zenpost's own number; published
 * and scheduled ones are never deleted, but Zenpost publishes nothing for an account without
 * access.
 */
function assetItems(
  assets: CancelRescueAssets | null | undefined,
  choice: CancellationChoice,
  t: TranslateFn,
): CancellationLosses["items"] {
  if (!assets || typeof assets !== "object") return [];
  const items: CancellationLosses["items"] = [];
  const contents = count(assets.contents);
  if (contents > 0) {
    const unpublished = count(assets.unpublished_contents);
    const days = count(assets.unpublished_kept_days);
    items.push({
      key: "contents",
      label:
        contents === 1
          ? t("integrated.cancelLossContentsOne")
          : t("integrated.cancelLossContents", { count: String(contents) }),
      ...(unpublished > 0 && days > 0
        ? {
            detail: t("integrated.cancelLossUnpublishedKept", {
              count: String(unpublished),
              days: String(days),
            }),
          }
        : {}),
    });
  }
  const scheduled = count(
    choice === "refund_now" ? assets.scheduled_contents : assets.scheduled_after_access_ends,
  );
  if (scheduled > 0) {
    items.push({
      key: "scheduled",
      label:
        scheduled === 1
          ? t("integrated.cancelLossScheduledOne")
          : t("integrated.cancelLossScheduled", { count: String(scheduled) }),
    });
  }
  if (assets.brand_identity === true) {
    items.push({ key: "brand_identity", label: t("integrated.cancelLossBrandIdentity") });
  }
  return items;
}

/**
 * What the cancel confirm screen lists as lost, worded for the customer's
 * choice: everything goes now with the refund, at the end of the paid period
 * when the renewal stops. What they built comes first, then the bonuses. Null
 * when there is nothing to list, so the screen never shows an empty box (and
 * stays as before when Zenpost sent nothing).
 */
export function buildCancellationLosses(args: {
  bonuses: CancelRescueBonus[] | null | undefined;
  zenlinksPage: CancelRescueZenlinksPage | null | undefined;
  assets?: CancelRescueAssets | null;
  choice: CancellationChoice;
  periodEndLabel: string;
  t: TranslateFn;
}): CancellationLosses | null {
  const { bonuses, zenlinksPage, assets, choice, periodEndLabel, t } = args;

  const seen = new Set<string>();
  const items: CancellationLosses["items"] = assetItems(assets, choice, t);
  for (const bonus of Array.isArray(bonuses) ? bonuses : []) {
    if (!bonus || typeof bonus.key !== "string" || !bonus.key || seen.has(bonus.key)) continue;
    seen.add(bonus.key);
    const name = bonusLabel(bonus.key, typeof bonus.name === "string" ? bonus.name : "", t);
    if (!name) continue;
    const pageUrl = bonus.key === "zenlinks" && zenlinksPage?.live ? displayPageUrl(zenlinksPage.url ?? "") : "";
    items.push({
      key: bonus.key,
      label: pageUrl ? t("integrated.cancelLossZenlinksLive", { name, url: pageUrl }) : name,
    });
  }
  if (items.length === 0) return null;

  let title: string;
  if (choice === "refund_now") title = t("integrated.cancelLossTitleNow");
  else if (periodEndLabel) title = t("integrated.cancelLossTitleAtDate", { date: periodEndLabel });
  else title = t("integrated.cancelLossTitleAtPeriodEnd");
  return { title, items };
}
